import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../../services/authService';
import { Button, Input, Alert } from '../ui';
import { useCountry } from '../../hooks/useCountry';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { theme, country: countryConfig, isSubdomain } = useCountry();
  const { t, i18n } = useTranslation();
  const colors = theme?.colors || {};

  // Set language from country config on subdomain
  useEffect(() => {
    if (isSubdomain && countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [isSubdomain, countryConfig, i18n]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authService.login(email, password);
      if (response.user?.is_staff || response.user?.is_superuser) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.login.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 mb-6 sm:mb-8 group">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))', boxShadow: theme ? `0 10px 25px -5px ${colors.primary}40` : undefined }}>
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className={`text-xl sm:text-2xl font-bold ${theme ? '' : 'gradient-text'}`} style={theme ? { color: colors.primary } : undefined}>RentyDoc</span>
          </Link>

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('auth.login.title')}</h1>
            <p className="text-gray-600 text-sm sm:text-base">
              {t('auth.login.noAccount')}{' '}
              <Link to="/register" className="font-semibold" style={{ color: colors.primary || 'var(--primary-600)' }}>
                {t('auth.login.signUpFree')}
              </Link>
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert type="error" className="mb-4 sm:mb-6" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <Input
              label={t('auth.login.emailLabel')}
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('auth.login.emailPlaceholder')}
              icon={(props) => (
                <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              )}
            />

            <Input
              label={t('auth.login.passwordLabel')}
              type="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('auth.login.passwordPlaceholder')}
            />

            {/* Remember Me & Forgot Password */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="flex items-center cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 border-2 border-gray-300 rounded peer-checked:border-primary-500 peer-checked:bg-primary-500 transition-colors" style={rememberMe && theme ? { borderColor: colors.primary, backgroundColor: colors.primary } : undefined}>
                    <svg
                      className={`w-3 h-3 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity ${rememberMe ? 'opacity-100' : 'opacity-0'}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">{t('auth.login.rememberMe')}</span>
              </label>

              <Link to="/forgot-password" className="text-sm font-medium" style={{ color: colors.primary || 'var(--primary-600)' }}>
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="lg"
              loading={loading}
              className="!py-3.5"
            >
              {t('auth.login.signIn')}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6 sm:my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t('auth.login.orContinueWith')}</span>
            </div>
          </div>

          {/* Social Login Buttons */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <button type="button" className="flex items-center justify-center px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200" disabled>
              <svg className="w-5 h-5 sm:mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">Google</span>
            </button>
            <button type="button" className="flex items-center justify-center px-3 sm:px-4 py-3 border-2 border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200" disabled>
              <svg className="w-5 h-5 sm:mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">GitHub</span>
            </button>
          </div>

          {/* Admin Link */}
          <div className="text-center mt-6 sm:mt-8">
            <a href="/admin/" className="text-sm text-gray-500 hover:text-gray-700">Admin Login</a>
          </div>
        </div>
      </div>

      {/* Right Side - Branding/Illustration (Hidden on mobile) */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden" style={{ background: theme ? colors.heroBg : undefined }}>
        {!theme && <div className="absolute inset-0 hero-gradient-bg" />}
        {/* Floating Shapes */}
        <div className="absolute w-96 h-96 rounded-full -top-48 -right-48 blur-3xl float-animation" style={{ backgroundColor: theme ? `${colors.primaryLight}40` : 'rgba(var(--primary-400-rgb), 0.3)' }} />
        <div className="absolute w-80 h-80 rounded-full bottom-1/4 -left-40 blur-3xl float-animation-delayed" style={{ backgroundColor: theme ? `${colors.accent || colors.secondary}30` : 'rgba(var(--accent-400-rgb), 0.3)' }} />

        <div className="relative z-10 flex flex-col items-center justify-center w-full p-16">
          <div className="glass-card p-8 mb-8 max-w-md text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, var(--primary-500), var(--accent-600))' }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Professional Rental Contracts</h2>
            <p className="text-gray-600">Create legally compliant rental agreements in minutes. Trusted by thousands of landlords worldwide.</p>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-4">
            <div className="trust-badge">
              <svg className="w-4 h-4 mr-2" style={{ color: theme ? colors.primary : 'var(--emerald-500)' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Secure</span>
            </div>
            <div className="trust-badge">
              <svg className="w-4 h-4 mr-2" style={{ color: colors.primary || 'var(--primary-500)' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Trusted</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
